import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactCrop, { type Crop, type PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { X } from 'lucide-react';
import { Button } from '../Button';

export interface RecortadorImagenProps {
  src: string;
  aspectRatio?: number;
  onSave: (croppedImage: string) => void;
  onCancel: () => void;
}

export function RecortadorImagen({ src, aspectRatio, onSave, onCancel }: RecortadorImagenProps) {
  const [crop, setCrop] = useState<Crop>({ unit: '%', x: 0, y: 0, width: 100, height: 100 });
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    if (aspectRatio === 1) {
      const size = Math.round(Math.min(width, height) * 0.8);
      const x = Math.round((width - size) / 2);
      const y = Math.round((height - size) / 2);
      const square: PixelCrop = { unit: 'px', x, y, width: size, height: size };
      setCrop(square);
      setCompletedCrop(square);
    } else {
      const full: PixelCrop = { unit: 'px', x: 0, y: 0, width: Math.round(width), height: Math.round(height) };
      setCompletedCrop(full);
    }
  };

  const getCroppedImg = async (image: HTMLImageElement, crop: PixelCrop, sourceSrc: string): Promise<string> => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('No 2d context');

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width;
    canvas.height = crop.height;

    ctx.drawImage(
      image,
      crop.x * scaleX, crop.y * scaleY,
      crop.width * scaleX, crop.height * scaleY,
      0, 0, crop.width, crop.height
    );

    const sourceMime = sourceSrc.match(/^data:(.*?);base64,/)?.[1] ?? '';
    const preservaTransparencia = sourceMime === 'image/png' || sourceMime === 'image/webp' || sourceMime === 'image/gif';
    const outputMime = preservaTransparencia ? sourceMime : 'image/jpeg';
    const outputQuality = outputMime === 'image/jpeg' ? 0.95 : undefined;

    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) { reject(new Error('Canvas is empty')); return; }
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result as string);
      }, outputMime, outputQuality);
    });
  };

  const handleSave = async () => {
    if (imgRef.current && completedCrop?.width && completedCrop?.height) {
      try {
        const result = await getCroppedImg(imgRef.current, completedCrop, src);
        onSave(result);
      } catch (e) {
        console.error('Error al recortar la imagen:', e);
      }
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[2000] p-5"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl max-w-[600px] w-full max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Ajustar imagen</h3>
          <button
            type="button"
            onClick={onCancel}
            className="w-8 h-8 rounded-md hover:bg-gray-100 flex items-center justify-center transition-colors"
          >
            <X size={20} className="text-gray-700" />
          </button>
        </div>

        <div className="p-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-5">
            <div className="text-xs font-semibold text-[#0050CB] mb-1.5">💡 Cómo ajustar tu imagen</div>
            <div className="text-[11px] text-gray-600 leading-relaxed">
              • Arrastra los nodos de las esquinas para redimensionar<br />
              • Arrastra los bordes para ajustar el área<br />
              • Mueve el área completa dentro de la imagen
            </div>
          </div>

          <div className="relative w-full flex justify-center mb-5">
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={aspectRatio}
              className="max-w-full max-h-[500px]"
            >
              <img
                ref={imgRef}
                src={src}
                alt="Recortar"
                onLoad={handleImageLoad}
                style={{ maxWidth: '100%', maxHeight: '500px' }}
              />
            </ReactCrop>
          </div>

          <div className="flex gap-3">
            <Button variant="secondary" size="md" onClick={onCancel} className="flex-1">
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleSave} className="flex-1">
              Aplicar recorte
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default RecortadorImagen;


